// SPDX-License-Identifier: MIT
// useSqlPanel Hook - Manages SQL panel state and DuckDB synchronization

import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AnyAction } from 'redux';
import { DuckDBService } from '../services/duckdb-service';
import { getTableSyncService } from '../services/table-sync';
import {
  toggleSqlPanel,
  setSqlPanelOpen,
  setSchemas,
  setSyncingTables,
  selectIsSqlPanelOpen,
  selectSchemas,
  selectIsSyncingTables,
  type RootStateWithSqlPanel,
} from '../sql-panel-slice';

// Helper to cast actions for dispatch
const asAction = <T extends { type: string }>(action: T): AnyAction =>
  action as unknown as AnyAction;

// Selector for Kepler.gl datasets
const selectKeplerDatasets = (state: any) => {
  try {
    return state?.keplerGl?.map?.visState?.datasets ?? {};
  } catch {
    return {};
  }
};

// Selector for Azure secure data flag
const selectAzureProviderDataLoaded = (state: any): boolean => {
  return Boolean(state?.azure?.providerDataLoaded);
};

/**
 * Hook for managing SQL panel state and synchronization
 */
export function useSqlPanel() {
  const dispatch = useDispatch();
  const isOpen = useSelector((state: RootStateWithSqlPanel) => selectIsSqlPanelOpen(state));
  const schemas = useSelector((state: RootStateWithSqlPanel) => selectSchemas(state));
  const isSyncing = useSelector((state: RootStateWithSqlPanel) => selectIsSyncingTables(state));
  const keplerDatasets = useSelector(selectKeplerDatasets);
  const hasAzureSecureData = useSelector(selectAzureProviderDataLoaded);

  // Track previous datasets to detect changes
  const prevDatasetsRef = useRef<Record<string, unknown>>({});
  const initializingRef = useRef(false);

  /**
   * Toggle the SQL panel open/closed
   */
  const toggle = useCallback(() => {
    dispatch(asAction(toggleSqlPanel()));
  }, [dispatch]);

  /**
   * Open the SQL panel
   */
  const open = useCallback(() => {
    dispatch(asAction(setSqlPanelOpen(true)));
  }, [dispatch]);

  /**
   * Close the SQL panel
   */
  const close = useCallback(() => {
    dispatch(asAction(setSqlPanelOpen(false)));
  }, [dispatch]);

  /**
   * Initialize DuckDB when panel opens
   */
  useEffect(() => {
    if (!isOpen || initializingRef.current) return;

    const initDuckDB = async () => {
      initializingRef.current = true;
      try {
        // Initialize DuckDB singleton
        await DuckDBService.getInstance();
        console.log('[useSqlPanel] DuckDB initialized');
      } catch (error) {
        console.error('[useSqlPanel] Failed to initialize DuckDB:', error);
      } finally {
        initializingRef.current = false;
      }
    };

    initDuckDB();
  }, [isOpen]);

  // Track if panel was just opened
  const wasOpenRef = useRef(false);

  /**
   * Sync Kepler.gl datasets to DuckDB when they change
   */
  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }

    // Check if datasets changed OR if panel was just opened
    const datasetIds = Object.keys(keplerDatasets);
    const prevDatasetIds = Object.keys(prevDatasetsRef.current);
    const panelJustOpened = !wasOpenRef.current && isOpen;
    wasOpenRef.current = true;

    const hasChanges =
      datasetIds.length !== prevDatasetIds.length ||
      datasetIds.some(id => !prevDatasetsRef.current[id]) ||
      prevDatasetIds.some(id => !keplerDatasets[id]);

    // Sync if datasets changed OR if panel just opened with existing datasets
    if (!hasChanges && !panelJustOpened) return;

    prevDatasetsRef.current = keplerDatasets;

    const syncDatasets = async () => {
      dispatch(asAction(setSyncingTables(true)));
      try {
        // Ensure DuckDB is ready
        await DuckDBService.getInstance();

        // Sync all datasets
        const tableSyncService = getTableSyncService();
        const visState = { datasets: keplerDatasets };

        // Check if dataset is from secure source (Azure)
        // All datasets considered secure when Azure provider data loaded
        const isSecureSource = (_datasetId: string): boolean => {
          return hasAzureSecureData;
        };

        const newSchemas = await tableSyncService.syncAllDatasets(visState, isSecureSource);
        dispatch(asAction(setSchemas(newSchemas)));

        console.log(`[useSqlPanel] Synced ${newSchemas.length} datasets to DuckDB`);
      } catch (error) {
        console.error('[useSqlPanel] Failed to sync datasets:', error);
      } finally {
        dispatch(asAction(setSyncingTables(false)));
      }
    };

    syncDatasets();
  }, [isOpen, keplerDatasets, hasAzureSecureData, dispatch]);

  return {
    isOpen,
    schemas,
    isSyncing,
    toggle,
    open,
    close,
  };
}

/**
 * Get the global toggle function for use in panel header
 */
export function setupSqlPanelToggle(toggleFn: () => void): void {
  (window as any).toggleSqlPanel = toggleFn;
}

// Extend Window type
declare global {
  interface Window {
    toggleSqlPanel?: () => void;
  }
}

export default useSqlPanel;
